import React, { useEffect, useState } from 'react';
import { BookOpenIcon, ChevronRightIcon, ShoppingCartIcon, PlusIcon, SearchIcon, FilterIcon, UploadIcon, DownloadIcon } from 'lucide-react';
import { CatalogList } from './catalogs/CatalogList';
import { CatalogForm } from './catalogs/CatalogForm';
import { SchemaBuilder } from './catalogs/SchemaBuilder';
import { ProductManager } from './catalogs/ProductManager';
import { ProductForm } from './catalogs/ProductForm';
import { flattenOrgUnits, sampleOrgUnits } from '../financial/sharedFinancialData';
// Define types
export type CatalogStatus = 'Active' | 'Inactive' | 'Archived';
export type DataType = 'string' | 'number' | 'boolean' | 'date' | 'dropdown' | 'currency' | 'url';
export interface Catalog {
  id: string;
  name: string;
  description: string;
  vendorId: string;
  vendorName: string;
  schemaId: string;
  activeFrom: string;
  activeTo: string | null;
  isActive: boolean;
  orgUnitIds: string[]; // Organisation units that can access this catalog
  productCount: number;
  status: CatalogStatus;
}
export interface CatalogSchema {
  id: string;
  name: string;
  catalogId: string;
  fields: CatalogField[];
}
export interface CatalogField {
  id: string;
  name: string;
  key: string;
  dataType: DataType;
  isRequired: boolean;
  options?: string[]; // For dropdown type
  defaultValue?: any;
  order: number;
}
export interface CatalogProduct {
  id: string;
  catalogId: string;
  name: string;
  vendorSku: string;
  price: number;
  currency: string;
  availableStock: number;
  manufacturer: string;
  category: string;
  hsCode?: string;
  warranty?: string;
  outerPackQty?: number;
  outerPackUnit?: string;
  innerPackQty?: number;
  innerPackUnit?: string;
  backordered?: number;
  qtyOrdered?: number;
  qtyInvoiced?: number;
  activeFrom: string;
  activeTo: string | null;
  isActive: boolean;
  customAttributes: Record<string, any>; // Dynamic fields based on schema
}
export interface Vendor {
  id: string;
  name: string;
  status: string;
}
export function CatalogsManagement({
  locale = 'en-US'
}) {
  // States for view management
  const [currentView, setCurrentView] = useState<'list' | 'create' | 'edit' | 'schema' | 'products' | 'product-form'>('list');
  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<CatalogSchema | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);
  // States for data
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [schemas, setSchemas] = useState<CatalogSchema[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  // States for filters
  const [filters, setFilters] = useState({
    vendor: '',
    orgUnit: '',
    status: ''
  });
  // Load sample data
  useEffect(() => {
    // In a real app, this would be API calls
    const sampleVendors: Vendor[] = [{
      id: 'vendor-001',
      name: 'Office Depot',
      status: 'Active'
    }, {
      id: 'vendor-002',
      name: 'Dell Technologies',
      status: 'Active'
    }, {
      id: 'vendor-003',
      name: 'PrintWorks Inc.',
      status: 'Active'
    }, {
      id: 'vendor-004',
      name: 'Global Electronics',
      status: 'Active'
    }, {
      id: 'vendor-005',
      name: 'Industrial Supplies Co.',
      status: 'Active'
    }];
    // Sample catalogs with OrgUnit IDs
    const sampleCatalogs: Catalog[] = [{
      id: 'catalog-001',
      name: 'Office Depot - Office Supplies',
      description: 'Standard office supplies from Office Depot',
      vendorId: 'vendor-001',
      vendorName: 'Office Depot',
      schemaId: 'schema-001',
      activeFrom: '2023-01-01',
      activeTo: '2024-12-31',
      isActive: true,
      orgUnitIds: ['ou-hq', 'ou-rnd', 'ou-latam-mx'],
      productCount: 1250,
      status: 'Active'
    }, {
      id: 'catalog-002',
      name: 'Dell - IT Equipment',
      description: 'IT hardware and accessories from Dell',
      vendorId: 'vendor-002',
      vendorName: 'Dell Technologies',
      schemaId: 'schema-002',
      activeFrom: '2023-02-15',
      activeTo: null,
      isActive: true,
      orgUnitIds: ['ou-hq', 'ou-apac-sg', 'ou-apac-tokyo'],
      productCount: 450,
      status: 'Active'
    }, {
      id: 'catalog-003',
      name: 'Industrial Supplies - Equipment',
      description: 'Industrial equipment and supplies',
      vendorId: 'vendor-005',
      vendorName: 'Industrial Supplies Co.',
      schemaId: 'schema-003',
      activeFrom: '2023-03-01',
      activeTo: '2023-09-30',
      isActive: false,
      orgUnitIds: ['ou-latam-mx', 'ou-latam-sp'],
      productCount: 320,
      status: 'Inactive'
    }];
    // Define sample schemas
    const sampleSchemas: CatalogSchema[] = [{
      id: 'schema-001',
      name: 'Office Supplies Schema',
      catalogId: 'catalog-001',
      fields: [{
        id: 'field-001',
        name: 'Color',
        key: 'color',
        dataType: 'string',
        isRequired: false,
        order: 0
      }, {
        id: 'field-002',
        name: 'Dimensions',
        key: 'dimensions',
        dataType: 'string',
        isRequired: false,
        order: 1
      }, {
        id: 'field-003',
        name: 'Weight (kg)',
        key: 'weight',
        dataType: 'number',
        isRequired: false,
        order: 2
      }, {
        id: 'field-004',
        name: 'Eco-Friendly',
        key: 'ecoFriendly',
        dataType: 'boolean',
        isRequired: false,
        order: 3
      }]
    }, {
      id: 'schema-002',
      name: 'IT Equipment Schema',
      catalogId: 'catalog-002',
      fields: [{
        id: 'field-005',
        name: 'Processor',
        key: 'processor',
        dataType: 'string',
        isRequired: false,
        order: 0
      }, {
        id: 'field-006',
        name: 'RAM (GB)',
        key: 'ram',
        dataType: 'number',
        isRequired: false,
        order: 1
      }, {
        id: 'field-007',
        name: 'Storage (GB)',
        key: 'storage',
        dataType: 'number',
        isRequired: false,
        order: 2
      }, {
        id: 'field-008',
        name: 'Operating System',
        key: 'os',
        dataType: 'dropdown',
        options: ['Windows', 'MacOS', 'Linux'],
        isRequired: false,
        order: 3
      }]
    }, {
      id: 'schema-003',
      name: 'Industrial Equipment Schema',
      catalogId: 'catalog-003',
      fields: [{
        id: 'field-009',
        name: 'Power Rating (W)',
        key: 'powerRating',
        dataType: 'number',
        isRequired: true,
        order: 0
      }, {
        id: 'field-010',
        name: 'Certification',
        key: 'certification',
        dataType: 'string',
        isRequired: false,
        order: 1
      }, {
        id: 'field-011',
        name: 'Hazardous Material',
        key: 'hazardous',
        dataType: 'boolean',
        isRequired: true,
        order: 2
      }]
    }];
    // Sample products for the first catalog
    const sampleProducts: CatalogProduct[] = [{
      id: 'product-001',
      catalogId: 'catalog-001',
      name: 'Premium Ballpoint Pen',
      vendorSku: 'OD-BP-001',
      price: 2.99,
      currency: 'USD',
      availableStock: 500,
      manufacturer: 'PaperMate',
      category: 'Writing Instruments',
      hsCode: '9608.10.00',
      warranty: '30 days',
      outerPackQty: 12,
      outerPackUnit: 'box',
      innerPackQty: 1,
      innerPackUnit: 'pen',
      backordered: 0,
      qtyOrdered: 100,
      qtyInvoiced: 100,
      activeFrom: '2023-01-01',
      activeTo: null,
      isActive: true,
      customAttributes: {
        color: 'Blue',
        dimensions: '14cm x 1cm',
        weight: 0.02,
        ecoFriendly: false
      }
    }, {
      id: 'product-002',
      catalogId: 'catalog-001',
      name: 'Recycled Copy Paper',
      vendorSku: 'OD-RP-002',
      price: 5.99,
      currency: 'USD',
      availableStock: 200,
      manufacturer: 'GreenPaper',
      category: 'Paper Products',
      hsCode: '4802.56.00',
      warranty: null,
      outerPackQty: 5,
      outerPackUnit: 'case',
      innerPackQty: 500,
      innerPackUnit: 'sheets',
      backordered: 0,
      qtyOrdered: 50,
      qtyInvoiced: 50,
      activeFrom: '2023-01-01',
      activeTo: null,
      isActive: true,
      customAttributes: {
        color: 'White',
        dimensions: '8.5in x 11in',
        weight: 2.3,
        ecoFriendly: true
      }
    }, {
      id: 'product-003',
      catalogId: 'catalog-001',
      name: 'Desk Organizer',
      vendorSku: 'OD-DO-003',
      price: 15.99,
      currency: 'USD',
      availableStock: 75,
      manufacturer: 'OfficePro',
      category: 'Desk Accessories',
      hsCode: '3926.10.00',
      warranty: '1 year',
      outerPackQty: 6,
      outerPackUnit: 'box',
      innerPackQty: 1,
      innerPackUnit: 'unit',
      backordered: 0,
      qtyOrdered: 30,
      qtyInvoiced: 30,
      activeFrom: '2023-01-01',
      activeTo: null,
      isActive: true,
      customAttributes: {
        color: 'Black',
        dimensions: '30cm x 20cm x 15cm',
        weight: 0.8,
        ecoFriendly: false
      }
    }];
    setVendors(sampleVendors);
    setSchemas(sampleSchemas);
    setCatalogs(sampleCatalogs);
    setProducts(sampleProducts);
  }, []);
  // Handle creating a new catalog
  const handleCreateCatalog = () => {
    setSelectedCatalog(null);
    setCurrentView('create');
  };
  // Handle editing a catalog
  const handleEditCatalog = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    setCurrentView('edit');
  };
  // Handle viewing/editing a schema
  const handleManageSchema = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    const schema = schemas.find(s => s.id === catalog.schemaId);
    setSelectedSchema(schema || null);
    setCurrentView('schema');
  };
  // Handle managing products
  const handleManageProducts = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    setCurrentView('products');
  };
  // Handle adding a new product
  const handleAddProduct = (catalog: Catalog) => {
    setSelectedCatalog(catalog);
    setSelectedProduct(null);
    setCurrentView('product-form');
  };
  // Handle editing a product
  const handleEditProduct = (product: CatalogProduct) => {
    setSelectedProduct(product);
    setCurrentView('product-form');
  };
  // Handle saving a catalog
  const handleSaveCatalog = (catalogData: Partial<Catalog>) => {
    if (selectedCatalog) {
      // Update existing catalog
      const updatedCatalog = {
        ...selectedCatalog,
        ...catalogData
      };
      setCatalogs(catalogs.map(c => c.id === selectedCatalog.id ? updatedCatalog : c));
    } else {
      // Create new catalog
      const newCatalog: Catalog = {
        id: `catalog-${catalogs.length + 1}`.padStart(10, '0'),
        name: catalogData.name || '',
        description: catalogData.description || '',
        vendorId: catalogData.vendorId || '',
        vendorName: vendors.find(v => v.id === catalogData.vendorId)?.name || '',
        schemaId: '',
        activeFrom: catalogData.activeFrom || new Date().toISOString().split('T')[0],
        activeTo: catalogData.activeTo || null,
        isActive: catalogData.isActive || false,
        orgUnitIds: catalogData.orgUnitIds || [],
        productCount: 0,
        status: catalogData.isActive ? 'Active' : 'Inactive'
      };
      // Create a new schema for this catalog
      const newSchema: CatalogSchema = {
        id: `schema-${schemas.length + 1}`.padStart(10, '0'),
        name: `${newCatalog.name} Schema`,
        catalogId: newCatalog.id,
        fields: []
      };
      newCatalog.schemaId = newSchema.id;
      setCatalogs([...catalogs, newCatalog]);
      setSchemas([...schemas, newSchema]);
      // Set the new catalog and schema as selected for schema editing
      setSelectedCatalog(newCatalog);
      setSelectedSchema(newSchema);
      setCurrentView('schema');
    }
  };
  // Handle saving a schema
  const handleSaveSchema = (schemaData: CatalogSchema) => {
    if (selectedSchema) {
      // Update existing schema
      setSchemas(schemas.map(s => s.id === selectedSchema.id ? schemaData : s));
    } else {
      // Create new schema
      setSchemas([...schemas, schemaData]);
    }
    setCurrentView('list');
  };
  // Handle saving a product
  const handleSaveProduct = (productData: Partial<CatalogProduct>) => {
    if (selectedProduct) {
      // Update existing product
      const updatedProduct = {
        ...selectedProduct,
        ...productData
      };
      setProducts(products.map(p => p.id === selectedProduct.id ? updatedProduct : p));
    } else if (selectedCatalog) {
      // Create new product
      const newProduct: CatalogProduct = {
        id: `product-${products.length + 1}`.padStart(10, '0'),
        catalogId: selectedCatalog.id,
        name: productData.name || '',
        vendorSku: productData.vendorSku || '',
        price: productData.price || 0,
        currency: productData.currency || 'USD',
        availableStock: productData.availableStock || 0,
        manufacturer: productData.manufacturer || '',
        category: productData.category || '',
        hsCode: productData.hsCode,
        warranty: productData.warranty,
        outerPackQty: productData.outerPackQty,
        outerPackUnit: productData.outerPackUnit,
        innerPackQty: productData.innerPackQty,
        innerPackUnit: productData.innerPackUnit,
        backordered: productData.backordered || 0,
        qtyOrdered: productData.qtyOrdered || 0,
        qtyInvoiced: productData.qtyInvoiced || 0,
        activeFrom: productData.activeFrom || new Date().toISOString().split('T')[0],
        activeTo: productData.activeTo || null,
        isActive: productData.isActive || true,
        customAttributes: productData.customAttributes || {}
      };
      setProducts([...products, newProduct]);
      // Update product count in the catalog
      setCatalogs(catalogs.map(c => {
        if (c.id === selectedCatalog.id) {
          return {
            ...c,
            productCount: c.productCount + 1
          };
        }
        return c;
      }));
    }
    setCurrentView('products');
  };
  // Handle toggling catalog status
  const handleToggleCatalogStatus = (catalogId: string) => {
    setCatalogs(catalogs.map(catalog => {
      if (catalog.id === catalogId) {
        const isActive = !catalog.isActive;
        return {
          ...catalog,
          isActive,
          status: isActive ? 'Active' : 'Inactive'
        };
      }
      return catalog;
    }));
  };
  // Handle toggling product status
  const handleToggleProductStatus = (productId: string) => {
    setProducts(products.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          isActive: !product.isActive
        };
      }
      return product;
    }));
  };
  // Handle filter changes
  const handleFilterChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  // Apply filters to catalogs
  const getFilteredCatalogs = () => {
    return catalogs.filter(catalog => {
      if (filters.vendor && catalog.vendorId !== filters.vendor) return false;
      if (filters.orgUnit && !catalog.orgUnitIds.includes(filters.orgUnit)) return false;
      if (filters.status && catalog.status !== filters.status) return false;
      return true;
    });
  };
  // Get products for a specific catalog
  const getCatalogProducts = (catalogId: string) => {
    return products.filter(product => product.catalogId === catalogId);
  };
  // Get schema for a specific catalog
  const getCatalogSchema = (catalogId: string) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return null;
    return schemas.find(s => s.id === catalog.schemaId) || null;
  };
  // Render the appropriate view
  const renderView = () => {
    switch (currentView) {
      case 'list':
        return <CatalogList catalogs={getFilteredCatalogs()} vendors={vendors} filters={filters} onFilterChange={handleFilterChange} onCreateCatalog={handleCreateCatalog} onEditCatalog={handleEditCatalog} onManageSchema={handleManageSchema} onManageProducts={handleManageProducts} onToggleStatus={handleToggleCatalogStatus} locale={locale} data-id="element-13837" />;
      case 'create':
      case 'edit':
        return <CatalogForm catalog={selectedCatalog} vendors={vendors} onSave={handleSaveCatalog} onCancel={() => setCurrentView('list')} locale={locale} data-id="element-13838" />;
      case 'schema':
        return selectedCatalog && <SchemaBuilder schema={selectedSchema} catalog={selectedCatalog} onSave={handleSaveSchema} onCancel={() => setCurrentView('list')} locale={locale} data-id="element-13839" />;
      case 'products':
        return selectedCatalog && <ProductManager catalog={selectedCatalog} products={getCatalogProducts(selectedCatalog.id)} schema={getCatalogSchema(selectedCatalog.id)} onAddProduct={() => handleAddProduct(selectedCatalog)} onEditProduct={handleEditProduct} onToggleStatus={handleToggleProductStatus} onBack={() => setCurrentView('list')} locale={locale} data-id="element-13840" />;
      case 'product-form':
        return selectedCatalog && <ProductForm product={selectedProduct} catalog={selectedCatalog} schema={getCatalogSchema(selectedCatalog.id)} onSave={handleSaveProduct} onCancel={() => setCurrentView('products')} locale={locale} data-id="element-13841" />;
      default:
        return null;
    }
  };
  // Get breadcrumbs based on current view
  const getBreadcrumbs = () => {
    const breadcrumbs = [{
      label: 'Procurement',
      icon: ShoppingCartIcon,
      onClick: () => null // This would navigate to procurement dashboard in a real app
    }, {
      label: 'Catalogs',
      icon: BookOpenIcon,
      onClick: () => setCurrentView('list')
    }];
    if (currentView === 'create') {
      breadcrumbs.push({
        label: 'Create Catalog',
        icon: PlusIcon,
        onClick: () => null
      });
    } else if (currentView === 'edit' && selectedCatalog) {
      breadcrumbs.push({
        label: selectedCatalog.name,
        icon: null,
        onClick: () => null
      });
    } else if (currentView === 'schema' && selectedCatalog) {
      breadcrumbs.push({
        label: selectedCatalog.name,
        icon: null,
        onClick: () => handleEditCatalog(selectedCatalog)
      });
      breadcrumbs.push({
        label: 'Schema Builder',
        icon: null,
        onClick: () => null
      });
    } else if ((currentView === 'products' || currentView === 'product-form') && selectedCatalog) {
      breadcrumbs.push({
        label: selectedCatalog.name,
        icon: null,
        onClick: () => handleEditCatalog(selectedCatalog)
      });
      if (currentView === 'products') {
        breadcrumbs.push({
          label: 'Products',
          icon: null,
          onClick: () => null
        });
      } else if (currentView === 'product-form') {
        breadcrumbs.push({
          label: 'Products',
          icon: null,
          onClick: () => handleManageProducts(selectedCatalog)
        });
        breadcrumbs.push({
          label: selectedProduct ? 'Edit Product' : 'Add Product',
          icon: null,
          onClick: () => null
        });
      }
    }
    return breadcrumbs;
  };
  return <div className="space-y-6" data-id="element-13842">
      <div className="mb-4" data-id="element-13843">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-13844">
          Catalogs Management
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13845">
          Manage vendor product catalogs and their availability across your
          organization
        </p>
      </div>

      {/* Main content */}
      {renderView()}
    </div>;
}